import { useState } from 'react';
import { ArrowDown, CheckCircle2, AlertTriangle, XCircle, MinusCircle, ChevronDown } from 'lucide-react';

/**
 * Représentation visuelle du traitement, dans l'esprit d'un éditeur d'ETL :
 * les étapes s'enchaînent de haut en bas, chacune montrant combien
 * d'enregistrements y sont entrés, en sont sortis, et combien ont été écartés.
 *
 * Ce composant ne connaît AUCUN batch. Il rend le tableau `steps` du contrat
 * commun : chaque batch décrit lui-même ses étapes et ses règles, et se trouve
 * documenté visuellement sans une ligne de code ici.
 *
 * Les règles métier sont volontairement rattachées à l'étape qui les applique :
 * c'est ce qui permet de comprendre le traitement sans ouvrir le code, et de
 * voir où exactement des enregistrements ont été perdus.
 */

const STATUS = {
  ok: { color: 'var(--success)', Icon: CheckCircle2, label: 'Terminée' },
  warn: { color: 'var(--warning)', Icon: AlertTriangle, label: 'Avec réserves' },
  error: { color: 'var(--danger)', Icon: XCircle, label: 'En échec' },
  skipped: { color: 'var(--text-muted)', Icon: MinusCircle, label: 'Non exécutée' },
};

function Counter({ label, value, tone }) {
  if (value == null) return null;
  const color =
    tone === 'danger' && value > 0
      ? 'var(--danger)'
      : tone === 'out'
        ? 'var(--text)'
        : 'var(--text-muted)';
  return (
    <div style={{ textAlign: 'center', minWidth: 58 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: 0.3 }}>{label}</div>
    </div>
  );
}

function Step({ step, index, isLast }) {
  const [open, setOpen] = useState(false);
  const s = STATUS[step.status ?? 'ok'] ?? STATUS.ok;
  const { Icon } = s;
  const hasDetail = Boolean(step.description || step.rules?.length);

  // Une perte d'enregistrements non imputée à un rejet explicite mérite d'être
  // visible : c'est souvent là que se cache un comportement mal compris.
  const lost =
    step.in != null && step.out != null
      ? step.in - step.out - (step.rejected ?? 0) - (step.skipped ?? 0)
      : 0;

  return (
    <>
      <div
        style={{
          border: `1px solid ${step.status === 'ok' || !step.status ? 'var(--glass-border)' : s.color}`,
          borderLeft: `3px solid ${s.color}`,
          borderRadius: 'var(--radius-sm)',
          background: 'var(--glass)',
          padding: '10px 12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: s.color,
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {index + 1}
          </div>

          <button
            type="button"
            onClick={() => hasDetail && setOpen((o) => !o)}
            style={{
              flex: 1,
              minWidth: 150,
              textAlign: 'left',
              border: 'none',
              background: 'transparent',
              color: 'var(--text)',
              cursor: hasDetail ? 'pointer' : 'default',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 13.5 }}>{step.label}</span>
            <Icon size={13} style={{ color: s.color, flexShrink: 0 }} title={s.label} />
            {hasDetail && (
              <ChevronDown
                size={13}
                style={{
                  color: 'var(--text-muted)',
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform .15s',
                }}
              />
            )}
          </button>

          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <Counter label="ENTRÉE" value={step.in} />
            <Counter label="SORTIE" value={step.out} tone="out" />
            {/* « Ignorés » et « rejets » ne disent PAS la même chose : le
                premier est un état normal (déjà à jour, sans objet), le second
                une anomalie. Les afficher dans la même colonne rendait des
                traitements sains alarmants. */}
            <Counter label="IGNORÉS" value={step.skipped} />
            <Counter label="REJETS" value={step.rejected} tone="danger" />
          </div>
        </div>

        {open && hasDetail && (
          <div style={{ marginTop: 10, paddingLeft: 34 }}>
            {step.description && (
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
                {step.description}
              </p>
            )}
            {step.rules?.length > 0 && (
              <ul
                style={{
                  margin: '8px 0 0',
                  paddingLeft: 16,
                  fontSize: 12,
                  color: 'var(--text-muted)',
                  lineHeight: 1.6,
                }}
              >
                {step.rules.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {!isLast && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6 }}>
          <ArrowDown size={14} style={{ color: 'var(--text-muted)', margin: '2px 0' }} />
          {lost > 0 && (
            <span style={{ fontSize: 10.5, color: 'var(--warning)' }}>
              −{lost} non transmis
            </span>
          )}
        </div>
      )}
    </>
  );
}

export default function PipelineDiagram({ steps }) {
  if (!steps?.length) return null;
  return (
    <>
      <h4 style={{ margin: '0 0 4px', fontSize: 14 }}>Traitement</h4>
      <p style={{ margin: '0 0 10px', fontSize: 11.5, color: 'var(--text-muted)' }}>
        Cliquez sur une étape pour voir ce qu'elle fait et les règles qu'elle applique.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 18 }}>
        {steps.map((s, i) => (
          <Step key={`${s.key}-${i}`} step={s} index={i} isLast={i === steps.length - 1} />
        ))}
      </div>
    </>
  );
}
